import React, { useState, useEffect } from 'react';
import { Search, Users, UserCheck, UserX, Loader2, AlertCircle, CheckCircle, Trash2, Ban, Shield } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import styles from './admin.module.css';

const AdminUsers = () => {
  const { theme, toggleTheme } = useTheme();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState({ show: false, userId: '', action: '', userName: '' });
  const API_URL = import.meta.env.VITE_API_URL;

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      setUsers(data.users || data);
      setFilteredUsers(data.users || data);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          email.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      setUpdatingUsers(prev => new Set([...prev, userId]));

      const response = await fetch(`${API_URL}/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update user role: ${response.status}`);
      }

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId ? { ...user, role: newRole } : user
        )
      );

      const actionText = newRole === 'reseller' ? 'promoted to reseller' : 'demoted to user';
      showNotification(`User successfully ${actionText}`, 'success');

    } catch (err) {
      showNotification(err.message || 'Failed to update user role', 'error');
      console.error('Error updating user role:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Delete user using /users/:id
  const deleteUser = async (userId) => {
    try {
      setUpdatingUsers(prev => new Set([...prev, userId]));

      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete user: ${response.status}`);
      }

      // Remove user from local state
      setUsers(prevUsers => prevUsers.filter(user => user._id !== userId));
      showNotification('User deleted successfully', 'success');

    } catch (err) {
      showNotification(err.message || 'Failed to delete user', 'error');
      console.error('Error deleting user:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      setShowConfirmDialog({ show: false, userId: '', action: '', userName: '' });
    }
  };

  // Toggle user status using /v1/users/:id/status
  const toggleUserStatus = async (userId) => {
    try {
      setUpdatingUsers(prev => new Set([...prev, userId]));

      const response = await fetch(`${API_URL}/v1/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to update user status: ${response.status}`);
      }

      const data = await response.json();

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user._id === userId ? { ...user, isActive: data.user.isActive } : user
        )
      );

      showNotification(data.message, 'success');

    } catch (err) {
      showNotification(err.message || 'Failed to update user status', 'error');
      console.error('Error updating user status:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      setShowConfirmDialog({ show: false, userId: '', action: '', userName: '' });
    }
  };

  // Handle confirm action
  const handleConfirmAction = () => {
    const { userId, action } = showConfirmDialog;
    if (action === 'delete') {
      deleteUser(userId);
    } else if (action === 'block' || action === 'unblock') {
      toggleUserStatus(userId);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'reseller':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRowClass = (user) => {
    if (user.role === 'reseller') return 'bg-blue-50';
    if (!user.isActive) return 'bg-red-50';
    return '';
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
        <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
        <div className="w-2 h-2 bg-red-400 rounded-full mr-1"></div>
        Blocked
      </span>
    );
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className={styles.userManagement}>
      {/* Header with Theme Toggle */}
      <div className={styles.header}>
        <h1 className={styles.title}>User Management</h1>
        <button
          onClick={toggleTheme}
          className={styles.themeToggle}
        >
          {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {error && <div className={`${styles.alert} ${styles.alertDanger}`}>{error}</div>}

      {/* Notification */}
      {notification.show && (
        <div className={`${styles.alert} ${notification.type === 'success' ? styles.alertSuccess : styles.alertDanger}`}>
          <div className="d-flex align-items-center">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 me-2" />
            ) : (
              <AlertCircle className="w-5 h-5 me-2" />
            )}
            {notification.message}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog.show && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h5 className={styles.modalTitle}>Confirm Action</h5>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setShowConfirmDialog({ show: false, userId: '', action: '', userName: '' })}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className="d-flex align-items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 me-2" />
                <h3 className="h5 mb-0">Confirm Action</h3>
              </div>
              <p className="text-muted mb-4">
                Are you sure you want to {showConfirmDialog.action} user "{showConfirmDialog.userName}"?
                {showConfirmDialog.action === 'delete' && ' This action cannot be undone.'}
              </p>
              <div className="d-flex gap-3">
                <button
                  onClick={handleConfirmAction}
                  className={showConfirmDialog.action === 'delete' ? styles.buttonDanger : styles.buttonWarning}
                >
                  Yes, {showConfirmDialog.action}
                </button>
                <button
                  onClick={() => setShowConfirmDialog({ show: false, userId: '', action: '', userName: '' })}
                  className={styles.buttonSecondary}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="row">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text">
                <Search className="w-4 h-4" />
              </span>
              <input
                type="text"
                className={`${styles.formControl} form-control`}
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h5 className={styles.cardTitle}>Users Overview</h5>
        </div>
        <div className={styles.cardBody}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading users...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className={`table ${styles.table}`}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className={`text-center py-5 ${styles.emptyState}`}>
                        <div className={styles.emptyStateIcon}>👥</div>
                        <p>No users found. {searchTerm ? `Try adjusting your search for "${searchTerm}".` : 'Add some users to get started!'}</p>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user._id} className={getRowClass(user)}>
                        <td>
                          <div className="fw-medium">{user.name || 'N/A'}</div>
                        </td>
                        <td>
                          <div className="text-muted small">{user.email || 'N/A'}</div>
                        </td>
                        <td>
                          <span className={`badge ${user.role === 'admin' ? 'bg-danger' : user.role === 'reseller' ? 'bg-primary' : 'bg-secondary'}`}>
                            {user.role === 'user' ? 'User' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td>
                          {getStatusBadge(user.isActive)}
                        </td>
                        <td>
                          {user.role !== 'admin' ? (
                            <div className="d-flex gap-2">
                              <button
                                onClick={() => updateUserRole(
                                  user._id,
                                  user.role === 'reseller' ? 'user' : 'reseller'
                                )}
                                disabled={updatingUsers.has(user._id)}
                                className={user.role === 'reseller' ? styles.buttonDanger : styles.buttonPrimary}
                                style={{ borderRadius: '8px', padding: '0.25rem 0.5rem', border: '1px solid transparent', fontSize: '0.75rem', fontWeight: '500' }}
                              >
                                {updatingUsers.has(user._id) ? (
                                  <Loader2 className="w-3 h-3 me-1 animate-spin" />
                                ) : user.role === 'reseller' ? (
                                  <UserX className="w-3 h-3 me-1" />
                                ) : (
                                  <UserCheck className="w-3 h-3 me-1" />
                                )}
                                {user.role === 'reseller' ? 'Demote' : 'Promote'}
                              </button>
                              <button
                                onClick={() => setShowConfirmDialog({
                                  show: true,
                                  userId: user._id,
                                  action: user.isActive ? 'block' : 'unblock',
                                  userName: user.name
                                })}
                                disabled={updatingUsers.has(user._id)}
                                className={user.isActive ? styles.buttonWarning : styles.buttonSuccess}
                                style={{ borderRadius: '8px', padding: '0.25rem 0.5rem', border: '1px solid transparent', fontSize: '0.75rem', fontWeight: '500' }}
                              >
                                {user.isActive ? <Ban className="w-3 h-3 me-1" /> : <Shield className="w-3 h-3 me-1" />}
                                {user.isActive ? 'Block' : 'Unblock'}
                              </button>
                              <button
                                onClick={() => setShowConfirmDialog({
                                  show: true,
                                  userId: user._id,
                                  action: 'delete',
                                  userName: user.name
                                })}
                                disabled={updatingUsers.has(user._id)}
                                className={styles.buttonDanger}
                                style={{ borderRadius: '8px', padding: '0.25rem 0.5rem', border: '1px solid transparent', fontSize: '0.75rem', fontWeight: '500' }}
                              >
                                <Trash2 className="w-3 h-3 me-1" />
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted small d-flex align-items-center">
                              <Shield className="w-4 h-4 me-1" />
                              Admin (Protected)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Stats Footer */}
      <div className="mt-3 text-muted small">
        Showing {filteredUsers.length} of {users.length} users
        {searchTerm && ` matching "${searchTerm}"`}
      </div>
    </div>
  );
};

export default AdminUsers;